import * as React from 'react';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, withMsal, WithMsalProps } from '@azure/msal-react';
import { InteractionStatus, PublicClientApplication } from "@azure/msal-browser";
import { MsalClientService } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../../global/Container';
import { IAButton } from '../button/Button';

export interface IMsalLoginProps {
  clientId: string;
  msalScope: string;
  redirectUri: string;
  redirectMethod: "popup" | "redirect";
  getTenantIdCallback?: (tenantId: string) => void;
}

export class IAMsalLogin extends React.Component<IMsalLoginProps> {
  private msalInstance: PublicClientApplication;

  constructor(props) {
    super(props);
    this.state = {
    };

    const msalConfig = {
      auth: {
        clientId: this.props.clientId,
        authority: "https://login.microsoftonline.com/organizations",
        redirectUri: this.props.redirectUri,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
      }
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  render() {
    const MsalLoginProtected = withMsal(MsalLogin);
    return (
      <MsalProvider instance={this.msalInstance}>
        {/* ts-ignore is required to align interfaces */}
        {/* @ts-ignore */}
        <MsalLoginProtected
          msalScope={this.props.msalScope}
          redirectMethod={this.props.redirectMethod}
          getTenantIdCallback={this.props.getTenantIdCallback}
        >
          {this.props.children}
        </MsalLoginProtected>
      </MsalProvider>
    );
  }
}

export interface IMsalLoginProtectedProps extends WithMsalProps {
  msalScope: string;
  redirectMethod: "popup" | "redirect";
  getTenantIdCallback?: (tenantId: string) => void;
}

export interface IMsalLoginProtectedState {
  hasRegisteredContainer: boolean;
}

class MsalLogin extends React.Component<IMsalLoginProtectedProps, IMsalLoginProtectedState> {  
  private loginRequest: { scopes: string[] };
  private _isMounted = false;

  constructor(props: IMsalLoginProtectedProps) {
    super(props);
    this.loginRequest = { scopes: [this.props.msalScope] };
    this.state = {
      hasRegisteredContainer: false
    };
  }

  componentDidMount(): void {
    this._isMounted = true;
  }
  
  componentWillUnmount(): void {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: IMsalLoginProtectedProps): void {
    if (!this._isMounted) return;
    if (prevProps.msalContext.inProgress === InteractionStatus.HandleRedirect && this.props.msalContext.inProgress === InteractionStatus.None) {
      this.props.msalContext.instance.acquireTokenSilent({
        ...this.loginRequest,
        account: this.props.msalContext.accounts[0]
      }).then(() => {
        this.signInSuccessful();
        this.forceUpdate();
      }).catch((e) => {
        console.error(e);
        this.signIn();
      });
    }
  }

  private signIn() {
    if (this.props.msalContext.inProgress === InteractionStatus.Startup || !this._isMounted) return;
    if (this.props.redirectMethod === "popup") {
      this.props.msalContext.instance.loginPopup(this.loginRequest)
        .then(() => this.signInSuccessful())
        .catch(e => this.signInUnsuccessful(e));
    }
    if (this.props.redirectMethod === "redirect") {
      this.props.msalContext.instance.loginRedirect(this.loginRequest)
        .then(() => this.signInSuccessful())
        .catch(e => this.signInUnsuccessful(e));
    }
  }

  private signInSuccessful() {
    if (!this._isMounted) return;
    const msalClientService: MsalClientService = new MsalClientService(this.props.msalContext.instance, this.props.msalContext.accounts, this.props.msalScope);
    sdkContainerInstance.asSingletonObject(MsalClientService, msalClientService);

    if (this.props.getTenantIdCallback && this.props.msalContext?.accounts[0]) {
      this.props.getTenantIdCallback(this.props.msalContext.accounts[0].tenantId);
    }

    const containerCheck = setInterval(() => {
      if (sdkContainerInstance.isRegistered(MsalClientService)) {
        this.setState({ hasRegisteredContainer: true });
        clearInterval(containerCheck);
      }
    }, 100);
  }

  private signInUnsuccessful(e: any) {
    console.error(e);
  }

  render() {
    return (
      <>
        <AuthenticatedTemplate>
          {this.state.hasRegisteredContainer && this.props.children}
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <div
            style={{
              padding: "20px 50px"
            }}
          >
            {this.props.msalContext.inProgress === InteractionStatus.Login
            ?
              <h1>Logging in...</h1> 
            :
              <IAButton
                label="Click here to log in"
                buttonColor="grey"
                onClick={() => this.signIn()}
              />
            }
          </div>
        </UnauthenticatedTemplate>
      </>
    )
  }
};